import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  Link,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as CsvIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { TimezoneService } from '../services/timezoneService';
import moment from 'moment-timezone';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';

interface MissingPunchRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  email: string;
  department: string;
  campaign: string;
  campaignId: string;
  date: string;
  missingType: 'clock_out' | 'break_end' | 'lunch_end';
  startEntry: {
    id: string;
    type: string;
    timestamp: string;
    timestampFormatted?: string;
    timestampTimeFormatted?: string;
  };
  entryId: string;
  notes?: string | null;
  correctedBy?: string | null;
  correctedAt?: string | null;
  isCorrected: boolean;
  isAutoClockOut?: boolean;
}

interface MissingPunchesFilters {
  startDate: string;
  endDate: string;
  userId: string;
  departmentId: string;
  campaignId: string;
}

const MissingPunchesReportPage: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canCreateTimeEntries = permissions.includes('time_entries.create');
  const canUpdateTimeEntries = permissions.includes('time_entries.update');
  const navigate = useNavigate();
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<MissingPunchRecord | null>(null);
  const [notesText, setNotesText] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Default to yesterday
  const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');

  const [filters, setFilters] = useState<MissingPunchesFilters>({
    startDate: yesterday,
    endDate: yesterday,
    userId: '',
    departmentId: '',
    campaignId: '',
  });

  // Fetch missing punches report data
  const { data: missingPunchesData, isLoading, error, refetch } = useQuery({
    queryKey: ['missingPunches', filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.userId) params.append('userId', filters.userId);
        if (filters.departmentId) params.append('departmentId', filters.departmentId);
        if (filters.campaignId) params.append('campaignId', filters.campaignId);

        const response = await apiClient.timeEntries.getMissingPunches(params.toString());
        if (!response.data.success) {
          throw new Error('Failed to load missing punches report');
        }
        return response.data;
      } catch (err: any) {
        console.error('Error fetching missing punches:', err);
        throw err;
      }
    },
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
    refetchInterval: false, // Manual refresh only (auto-refresh handled by scheduled task)
    retry: 1, // Retry once on failure
  });

  // Fetch users for filter dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.users.getAll(),
    enabled: ['manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch departments for filter dropdown
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => apiClient.departments.getAll(),
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch campaigns for filter dropdown
  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns', filters.startDate, filters.endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: filters.startDate,
      includeWithActivityTo: filters.endDate,
    }),
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Note: Auto-refresh at 8 AM and email notifications are handled by backend scheduled tasks
  // This frontend just provides manual refresh capability

  const handleFilterChange = (field: keyof MissingPunchesFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: yesterday,
      endDate: yesterday,
      userId: '',
      departmentId: '',
      campaignId: '',
    });
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleNotesClick = (record: MissingPunchRecord) => {
    setSelectedRecord(record);
    // Extract just the note text (remove the "CORRECTED by..." header if present)
    const noteText = record.notes?.includes('CORRECTED by') 
      ? record.notes.split('\n').slice(1).join('\n').trim()
      : (record.notes || '');
    setNotesText(noteText);
    setNotesDialogOpen(true);
  };

  const handleNotesSave = async () => {
    if (!selectedRecord) return;
    
    try {
      const response = await fetch(`/api/time-entries/missing-punches/${selectedRecord.entryId}/note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ note: notesText })
      });

      const result = await response.json();
      
      if (result.success) {
        // Refresh the data
        refetch();
        setNotesDialogOpen(false);
        setSelectedRecord(null);
        setNotesText('');
      } else {
        alert('Failed to save note: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    }
  };

  const handleAddEntryClick = (record: MissingPunchRecord) => {
    // Navigate to add entry with pre-filled data
    // Build query parameters for pre-filling the add entry form
    const params = new URLSearchParams();
    params.set('action', 'add-entry');
    params.set('employeeId', record.employeeId); // Use employeeId, will be looked up in TimeRecords page
    params.set('employeeEmail', record.email); // Include email for lookup
    params.set('campaignId', record.campaignId);
    params.set('entryType', record.missingType);
    params.set('date', record.date);
    // Pre-fill timestamp from start entry or use current time
    // For missing pairs, timestamp should be after the start entry time
    if (record.startEntry?.timestamp) {
      // Use the start entry timestamp as a reference, but the user can adjust
      params.set('startEntryTimestamp', record.startEntry.timestamp);
    }
    navigate(`/management/time-records?${params.toString()}`);
  };

  const handleEditClick = (record: MissingPunchRecord) => {
    // For auto clock-out entries, navigate to edit the existing entry
    navigate(`/management/time-records?editEntry=${record.entryId}&date=${record.date}&isAutoClockOut=true`);
  };

  const getMissingTypeLabel = (type: string) => {
    switch (type) {
      case 'clock_out': return 'Missing Clock Out';
      case 'break_end': return 'Missing Break End';
      case 'lunch_end': return 'Missing Lunch End';
      default: return type;
    }
  };

  const getMissingTypeColor = (type: string) => {
    switch (type) {
      case 'clock_out': return 'error';
      case 'break_end': return 'warning';
      case 'lunch_end': return 'info';
      default: return 'default';
    }
  };

  const exportToCSV = () => {
    if (!missingPunchesData?.data) return;

    const headers = ['Employee ID', 'Employee Name', 'Email', 'Department', 'Campaign', 'Date', 'Missing Type', 'Start Time', 'Notes', 'Status'];
    const rows = missingPunchesData.data.map((record: MissingPunchRecord) => [
      record.employeeId,
      record.employeeName,
      record.email,
      record.department,
      record.campaign,
      record.date,
      getMissingTypeLabel(record.missingType),
      record.startEntry.timestampFormatted || TimezoneService.formatDateTimeConsistent(record.startEntry.timestamp, TimezoneService.getUserTimezone()),
      record.notes || '',
      record.isCorrected ? 'Corrected' : 'Missing',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any[]) => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `missing-punches-${filters.startDate}-to-${filters.endDate}.csv`;
    link.click();
    handleExportMenuClose();
  };

  const exportToExcel = async () => {
    if (!missingPunchesData?.data) return;

    const XLSX = await import('xlsx');
    const wsData = [
      ['Employee ID', 'Employee Name', 'Email', 'Department', 'Campaign', 'Date', 'Missing Type', 'Start Time', 'Notes', 'Status'],
      ...missingPunchesData.data.map((record: MissingPunchRecord) => [
        record.employeeId,
        record.employeeName,
        record.email,
        record.department,
        record.campaign,
        record.date,
        getMissingTypeLabel(record.missingType),
        record.startEntry.timestampFormatted || TimezoneService.formatDateTimeConsistent(record.startEntry.timestamp, TimezoneService.getUserTimezone()),
        record.notes || '',
        record.isCorrected ? 'Corrected' : 'Missing',
      ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Missing Punches');
    XLSX.writeFile(wb, `missing-punches-${filters.startDate}-to-${filters.endDate}.xlsx`);
    handleExportMenuClose();
  };

  const exportToPDF = async () => {
    if (!missingPunchesData?.data) return;

    const jsPDF = (await import('jspdf')).default;
    const autoTable = (await import('jspdf-autotable')).default;
    const doc = new jsPDF();

    doc.text('Missing Punches Report', 14, 10);
    doc.text(`Date Range: ${filters.startDate} to ${filters.endDate}`, 14, 16);

    const tableData = missingPunchesData.data.map((record: MissingPunchRecord) => [
      record.employeeId,
      record.employeeName,
      record.department,
      record.campaign,
      record.date,
      getMissingTypeLabel(record.missingType),
      record.startEntry.timestampFormatted || TimezoneService.formatDateTimeConsistent(record.startEntry.timestamp, TimezoneService.getUserTimezone()),
      record.isCorrected ? 'Corrected' : 'Missing',
    ]);

    autoTable(doc, {
      head: [['Employee ID', 'Name', 'Department', 'Campaign', 'Date', 'Missing Type', 'Start Time', 'Status']],
      body: tableData,
      startY: 22,
    });

    doc.save(`missing-punches-${filters.startDate}-to-${filters.endDate}.pdf`);
    handleExportMenuClose();
  };

  // Extract data from response structure
  const records = missingPunchesData?.data || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Missing Punches Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <ButtonGroup variant="contained">
            <Button
              onClick={handleExportMenuOpen}
              endIcon={<ArrowDropDownIcon />}
            >
              Export
            </Button>
          </ButtonGroup>
          {canShareReports && (
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setShareDialogOpen(true)}
              disabled={!missingPunchesData?.data || missingPunchesData.data.length === 0}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Share
            </Button>
          )}
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={exportToCSV}>
              <ListItemIcon>
                <CsvIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export to CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={exportToExcel}>
              <ListItemIcon>
                <ExcelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export to Excel</ListItemText>
            </MenuItem>
            <MenuItem onClick={exportToPDF}>
              <ListItemIcon>
                <PdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export to PDF</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {['manager', 'administrator', 'super_user'].includes(user?.role || '') && (
                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    options={usersData?.data?.data || []}
                    value={filters.userId ? usersData?.data?.data?.find((u: any) => u.id === filters.userId) || null : null}
                    onChange={(event, newValue) => {
                      handleFilterChange('userId', newValue?.id || '');
                    }}
                    getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName}` : ''}
                    renderInput={(params) => (
                      <TextField {...params} label="Employee" placeholder="Search employee..." />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.firstName} {option.lastName}
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No employees found"
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const searchLower = inputValue.toLowerCase();
                      return options.filter(option =>
                        `${option.firstName} ${option.lastName}`.toLowerCase().includes(searchLower) ||
                        (option.employeeId && option.employeeId.toLowerCase().includes(searchLower))
                      );
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={2}>
                <SearchableSelect
                  label="Department"
                  value={filters.departmentId}
                  onChange={(value) => handleFilterChange('departmentId', String(value))}
                  options={(departmentsData?.data?.data || []).map((dept: any) => ({
                    value: dept.id,
                    label: dept.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <SearchableSelect
                  label="Campaign"
                  value={filters.campaignId}
                  onChange={(value) => handleFilterChange('campaignId', String(value))}
                  options={(campaignsData?.data?.data || []).map((camp: any) => ({
                    value: camp.id,
                    label: camp.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleResetFilters}
                  sx={{ height: '56px' }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load missing punches report. Please try again.
          {process.env.NODE_ENV === 'development' && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Error: {error instanceof Error ? error.message : 'Unknown error'}
            </Typography>
          )}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Total Missing Punches: {records.length}
            </Typography>
            
            {records.length === 0 ? (
              <Alert severity="info">No missing punches found for the selected date range.</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Employee</strong></TableCell>
                      <TableCell><strong>Department</strong></TableCell>
                      <TableCell><strong>Campaign</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Missing Type</strong></TableCell>
                      <TableCell><strong>Start Time</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Notes</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((record: MissingPunchRecord) => (
                      <TableRow
                        key={record.id}
                        sx={{
                          backgroundColor: record.isCorrected ? 'grey.100' : 'rgba(211, 47, 47, 0.1)',
                          '&:hover': {
                            backgroundColor: record.isCorrected ? 'grey.200' : 'rgba(211, 47, 47, 0.15)',
                          },
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {record.employeeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {record.employeeId} • {record.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{record.department}</TableCell>
                        <TableCell>{record.campaign}</TableCell>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>
                          <Chip
                            label={getMissingTypeLabel(record.missingType)}
                            color={getMissingTypeColor(record.missingType) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {record.startEntry.timestampFormatted || TimezoneService.formatDateTimeConsistent(
                            record.startEntry.timestamp,
                            TimezoneService.getUserTimezone()
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Chip
                              label={record.isCorrected ? 'Corrected' : 'Missing'}
                              color={record.isCorrected ? 'default' : 'error'}
                              size="small"
                              sx={{ mb: 0.5 }}
                            />
                            {record.isCorrected && record.correctedBy && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                By: {record.correctedBy}
                              </Typography>
                            )}
                            {record.isCorrected && record.correctedAt && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {TimezoneService.formatDateTimeConsistent(record.correctedAt, TimezoneService.getUserTimezone())}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={record.notes ? (record.notes.length > 50 ? record.notes : '') : (canUpdateTimeEntries ? 'No notes - click to add' : 'No notes')}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                maxWidth: 200, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: canUpdateTimeEntries ? 'pointer' : 'default',
                                color: record.notes ? 'text.primary' : 'text.secondary',
                                fontStyle: record.notes ? 'normal' : 'italic'
                              }}
                              onClick={canUpdateTimeEntries ? () => handleNotesClick(record) : undefined}
                            >
                              {record.notes 
                                ? (record.notes.includes('CORRECTED by') 
                                    ? record.notes.split('\n').slice(1).join('\n') || 'Corrected (no details)'
                                    : record.notes)
                                : (canUpdateTimeEntries ? 'Click to add note' : 'No notes')}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {(canUpdateTimeEntries || canCreateTimeEntries) && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {record.isAutoClockOut ? (
                                canUpdateTimeEntries && (
                                  <Tooltip title="Edit Auto Clock-Out Entry">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditClick(record)}
                                      color="primary"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )
                              ) : (
                                canCreateTimeEntries && (
                                  <Tooltip title="Add Missing Entry">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAddEntryClick(record)}
                                      color="primary"
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Note/Correction for {selectedRecord?.employeeName}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Adding a note will mark this missing punch as "Corrected". The note will include who corrected it and when.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes/Correction Details"
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            placeholder="Add a note explaining the correction or reason for the missing punch..."
            sx={{ mt: 1 }}
          />
          {selectedRecord && (
            <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Missing:</strong> {getMissingTypeLabel(selectedRecord.missingType)}<br />
                <strong>Date:</strong> {selectedRecord.date}<br />
                <strong>Start Time:</strong> {selectedRecord.startEntry.timestampFormatted || TimezoneService.formatDateTimeConsistent(selectedRecord.startEntry.timestamp, TimezoneService.getUserTimezone())}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNotesDialogOpen(false);
            setSelectedRecord(null);
            setNotesText('');
          }}>Cancel</Button>
          <Button onClick={handleNotesSave} variant="contained">Save Note & Mark as Corrected</Button>
        </DialogActions>
      </Dialog>

      {/* Share Report Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="missing-punches"
          reportFilters={filters}
          reportData={missingPunchesData?.data || []}
          reportTitle="Missing Punches Report"
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default MissingPunchesReportPage;

